// Utils
import { getEmail, userAccountIsRemoved } from '../../../../../common/users/userHelper';
import { createDeepSelector } from '../../../../utils/milanoteReselect/milanoteReselect';
import { hasImage } from '../../../../../common/users/utils/userPropertyUtils';
import { isUserActiveOnCurrentBoard } from '../../../../components/userAvatar/userAvatarUtils';
import { isBlocked } from '../../../../../common/permissions/permissionUtil';
import { asObject } from '../../../../../common/utils/immutableHelper';

// Selectors
import { currentBoardEditorsSelector } from './popup/editing/boardEditorPanelSelector';
import { getCurrentBoardId } from '../../../../element/selectors/currentBoardSelector';

const EMPTY_ARRAY = [];

export const sortBoardEditorsForAvatars = (editorA, editorB) => {
    // Send the current user to the bottom (as they're the only one that will have a root board ID)
    if (editorA.isCurrentUser) return 1;
    if (editorB.isCurrentUser) return -1;

    // Send deleted accounts to the bottom
    const editorAIsRemoved = userAccountIsRemoved(editorA.user);
    const editorBIsRemoved = userAccountIsRemoved(editorB.user);
    if (editorAIsRemoved !== editorBIsRemoved) return editorAIsRemoved ? 1 : -1;

    // Show users currently on the board higher up
    if (editorA.currentlyViewing !== editorB.currentlyViewing) return editorA.currentlyViewing ? -1 : 1;

    // Otherwise, prioritise editors with images
    const editorAHasImage = hasImage(editorA.user);
    const editorBHasImage = hasImage(editorB.user);
    if (editorAHasImage !== editorBHasImage) return editorAHasImage ? -1 : 1;

    // Finally sort based on the email address
    const emailA = getEmail(editorA.user);
    const emailB = getEmail(editorB.user);
    return (emailA && emailA.localeCompare(emailB)) || 0;
};

export const currentBoardEditorsAvatarInfoSelector = createDeepSelector(
    currentBoardEditorsSelector,
    getCurrentBoardId,
    (currentBoardEditors, currentBoardId) => {
        if (currentBoardEditors.length <= 1) return EMPTY_ARRAY;

        return (
            currentBoardEditors
                .filter((editor) => !isBlocked(editor.permission))
                .sort(sortBoardEditorsForAvatars)
                // The avatar components require user objects
                .map((editor) => {
                    const user = editor?.user;

                    if (!user) return null;

                    const { _id, image, name, isGuest, isAnonymous, email } = asObject(user) || {};

                    // To avoid unnecessary re-renders, we only take the used properties and instead
                    // of passing a user currentBoardId value we instead just pass the boolean
                    const isActiveOnCurrentBoard = isUserActiveOnCurrentBoard(user, currentBoardId);

                    return { _id, image, name, isGuest, isAnonymous, email, isActiveOnCurrentBoard };
                })
        );
    },
);
